import { z } from 'zod';
import {
  cardEditSchema,
  cardTagSchema,
  instagramUsernameSchema,
  layerSchema,
  textLayerSchema,
} from './domain/card-edit.js';
import {
  BRIEF_MAX,
  BILLING_INTERVALS,
  CHANNELS,
  DEFAULT_INTERVAL,
  FORMATS,
  GOALS,
  MEDIA_MODES,
  PLANS,
  SCENE_KEYS,
} from './constants.js';
import { SCHEDULE_TIME_RE } from './domain/time.js';
import { postTemplateSchema } from './domain/template.js';
import { hostVideoCopySchema, generationOperationSchema } from './domain/generation-plan.js';

export const channelSchema = z.enum(CHANNELS);
export const formatSchema = z.enum(FORMATS);
export const goalSchema = z.enum(GOALS);
export const mediaModeSchema = z.enum(MEDIA_MODES);
export const planSchema = z.enum(PLANS);
export const billingIntervalSchema = z.enum(BILLING_INTERVALS);
export const sceneSchema = z.enum(SCENE_KEYS as [string, ...string[]]);

export const listingUrlSchema = z
  .string()
  .trim()
  .max(2048)
  .transform((s) => (s && !/^https?:\/\//i.test(s) ? `https://${s}` : s))
  .refine((s) => s === '' || z.url().safeParse(s).success, 'Enter a valid listing URL');

export const startImportSchema = z.object({
  url: listingUrlSchema.optional().default(''),
  /** The host's zone while importing: the property's clock until the listing's location says better. */
  timezone: z.string().max(64).refine(isTimeZone, 'Unknown timezone').optional(),
});

export const updatePropertySchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  /** Matches the importer cap (DESCRIPTION_MAX) so a full listing survives Read & edit. */
  description: z.string().trim().max(10_000).optional(),
  bookingUrl: z.url().max(2048).nullable().optional(),
  details: z
    .array(z.object({ label: z.string().min(1).max(80), enabled: z.boolean() }))
    .max(40)
    .optional(),
  /** Moves this media item to the front of the property's photos. */
  heroMediaId: z.string().min(1).max(64).optional(),
  /** The clock everything scheduled on this property is read on. */
  timezone: z.string().max(64).refine(isTimeZone, 'Unknown timezone').nullable().optional(),
});

/** A booking the host attributes to social. `bookedOn` is a calendar day, YYYY-MM-DD. */
export const logBookingSchema = z.object({
  /** A booking is money in: never negative, and never nothing. */
  valueCents: z.number().int().min(1).max(10_000_000),
  bookedOn: z.iso.date(),
  postId: z.string().min(1).max(64).nullable().optional(),
  channel: channelSchema.nullable().optional(),
  note: z.string().trim().max(280).optional().default(''),
});
export type LogBookingInput = z.input<typeof logBookingSchema>;

export const claimPropertySchema = z.object({
  propertyId: z.string().min(1),
});

export const subscribeSchema = z.object({
  plan: planSchema,
  paymentMethodId: z.string().min(1).max(200),
  interval: billingIntervalSchema.default(DEFAULT_INTERVAL),
});

/** Puts a property on a tier of the account's subscription. */
export const attachPlanSchema = z.object({ plan: planSchema });

/** Whole credit packs, bought on the card on file. */
export const buyPackSchema = z.object({
  packs: z.number().int().min(1).max(20),
});

export const updatePaymentMethodSchema = z.object({
  paymentMethodId: z.string().min(1).max(200),
});

/** Omitting `interval` keeps the subscription's current billing period. */
export const changePlanSchema = z.object({
  plan: planSchema,
  interval: billingIntervalSchema.optional(),
});

export const createPostSchema = z.object({
  format: formatSchema,
  goal: goalSchema,
  brief: z.string().trim().min(1, 'Tell us what the post is about').max(BRIEF_MAX),
  channels: z.array(channelSchema).max(4).default([]),
  mediaMode: mediaModeSchema.default('photos'),
  referenceIds: z.array(z.string()).max(12).default([]),
  /** Media the host added themself, in order: used exactly. */
  pinnedIds: z.array(z.string()).max(10).default([]),
  /** The form of an Inspiration post to follow; the property supplies the substance. */
  template: postTemplateSchema.optional(),
  /** The Inspiration post it follows, for "Made after". */
  inspirationPostId: z.string().max(64).optional(),
});

export const generationTargetSchema = z.enum(['copy', 'visuals', 'whole']);
export const generateSchema = z.object({
  note: z.string().max(12_000).optional().default(''),
  /** What to remake. Left out, the note decides (a "brighter" is pictures, a "shorter" is words). */
  target: generationTargetSchema.optional(),
  /** Explicit editor action. Omit for ambiguous free text so the planner can classify it once. */
  operation: generationOperationSchema.optional(),
  hostVideoCopy: hostVideoCopySchema.optional(),
  /** Stable card ids where available; legacy cards use their zero-based index as a string. */
  targetCardIds: z.array(z.string().min(1).max(64)).max(10).optional().default([]),
  /** Preflight only: persist/return the plan without spending credits or submitting providers. */
  dryRun: z.boolean().optional().default(false),
  /** Opts a modern caller into immutable planning; absent keeps the legacy route unchanged. */
  structured: z.boolean().optional().default(false),
});
export const generationAcceptanceSchema = z.strictObject({
  acceptance: z.enum(['accepted', 'rejected']),
});

const webUrl = z
  .string()
  .trim()
  .max(500)
  .url()
  .refine((u) => /^https?:\/\//i.test(u), 'Use a web address that starts with http:// or https://');

export const postLinkSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('book') }),
  z.object({ mode: z.literal('custom'), url: webUrl }),
  z.object({ mode: z.literal('off') }),
]);
export type PostLinkInput = z.infer<typeof postLinkSchema>;

/**
 * What the host settled on for a reel before its finishing render: the words (a layer, or none)
 * and whether it goes out with no music. Kept on the post, so a reload or another device resumes.
 */
export const reelChoicesSchema = z.strictObject({
  words: z
    .strictObject({
      layer: textLayerSchema.nullable(),
      /** One line per storyboard scene, in the layer's style; absent = the one line throughout. */
      lines: z.array(z.string().max(80)).max(12).optional(),
    })
    .nullable()
    .optional(),
  silent: z.boolean().optional(),
});
export type ReelChoices = z.infer<typeof reelChoicesSchema>;

export const updatePostSchema = z.object({
  /** Brief-step settings, so a draft edited before its next run is regenerated as edited. */
  brief: z.string().trim().min(1).max(BRIEF_MAX).optional(),
  goal: goalSchema.optional(),
  referenceIds: z.array(z.string()).max(12).optional(),
  pinnedIds: z.array(z.string()).max(10).optional(),
  template: postTemplateSchema.nullable().optional(),
  inspirationPostId: z.string().max(64).nullable().optional(),
  caption: z.string().max(2200).optional(),
  /** Where the caption's link goes, or that there is none. Rewrites the caption's link line. */
  link: postLinkSchema.optional(),
  currentVersionId: z.string().optional(),
  format: formatSchema.optional(),
  mediaMode: mediaModeSchema.optional(),
  channels: z.array(channelSchema).max(4).optional(),
  collaborators: z.array(instagramUsernameSchema).max(3).optional(),
  location: z
    .strictObject({ id: z.string().min(1).max(100), name: z.string().trim().min(1).max(200) })
    .nullable()
    .optional(),
  reelChoices: reelChoicesSchema.nullable().optional(),
});

/** TikTok privacy levels, as the Content Posting API names them. */
export const TIKTOK_PRIVACY_LEVELS = [
  'PUBLIC_TO_EVERYONE',
  'MUTUAL_FOLLOW_FRIENDS',
  'FOLLOWER_OF_CREATOR',
  'SELF_ONLY',
] as const;
export type TikTokPrivacyLevel = (typeof TIKTOK_PRIVACY_LEVELS)[number];

/**
 * What the host confirmed on the share sheet before a TikTok direct post. TikTok's rules require
 * the creator to see and choose these themselves, so we send exactly what they picked.
 */
export const tiktokOptionsSchema = z
  .object({
    privacyLevel: z.enum(TIKTOK_PRIVACY_LEVELS),
    allowComment: z.boolean(),
    allowDuet: z.boolean(),
    allowStitch: z.boolean(),
    /** Promoting the host's own place. TikTok labels the post "Promotional content". */
    yourBrand: z.boolean().default(false),
    /** A paid partnership with someone else. TikTok labels it "Paid partnership". */
    brandedContent: z.boolean().default(false),
    /** Posts saved before TikTok's two-checkbox rule kept one flag; it reads as `yourBrand`. */
    promotesOwnBusiness: z.boolean().optional(),
    /**
     * TikTok wants the creator to expressly consent to its Music Usage Confirmation, and to the
     * Branded Content Policy when a partnership is declared. The share sheet will not hand these
     * options over until it is ticked; rows saved before it existed default to false and still
     * publish, because they were confirmed under the rules of the day.
     */
    consented: z.boolean().default(false),
  })
  // TikTok: "Branded content visibility cannot be set to private."
  .refine((o) => !(o.brandedContent && o.privacyLevel === 'SELF_ONLY'), {
    message: 'Branded content visibility cannot be set to private',
    path: ['privacyLevel'],
  });
export type TikTokOptions = z.infer<typeof tiktokOptionsSchema>;

/**
 * What TikTok is told about commercial content. Folds the single flag older posts stored into
 * the "your brand" half, so a post scheduled before the second checkbox still publishes right.
 */
export function tiktokDisclosure(o: TikTokOptions): {
  brandOrganic: boolean;
  brandedContent: boolean;
} {
  // A row stored before these fields existed has neither, so read every flag strictly: TikTok
  // drops an undefined toggle from the request instead of sending false.
  return {
    brandOrganic: o.yourBrand === true || o.promotesOwnBusiness === true,
    brandedContent: o.brandedContent === true,
  };
}

/** True when the runtime's Intl knows the IANA zone name (so it can be used to place a wall clock). */
export function isTimeZone(name: string): boolean {
  if (!name) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name });
    return true;
  } catch {
    return false;
  }
}

export const schedulePostSchema = z.object({
  /** ISO date (yyyy-mm-dd) in the host's local calendar */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** "h:mm AM/PM": one of the presets or any minute of the day. */
  time: z.string().regex(SCHEDULE_TIME_RE, 'Pick a time like 6:30 PM'),
  /** The host's IANA zone, which `date` + `time` are read in. Absent from older clients: UTC. */
  timezone: z.string().max(64).refine(isTimeZone, 'Unknown timezone').optional(),
  channels: z.array(channelSchema).min(1, 'Pick at least one channel').max(4),
  publishNow: z.boolean().default(false),
  /** Required by TikTok's rules whenever TT is one of the channels. */
  tiktok: tiktokOptionsSchema.optional(),
});

export const mediaLinkSchema = z.object({
  url: z.url().max(2048),
  label: z.string().trim().max(80).optional(),
});

/** Renaming a media item: the name the host sees in the library and the inspector. */
export const mediaUpdateSchema = z.object({
  label: z.string().trim().min(1, 'Give it a name').max(80),
});

export const paginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export type StartImportInput = z.infer<typeof startImportSchema>;
export type UpdatePropertyInput = z.infer<typeof updatePropertySchema>;
export type CreatePostInput = z.infer<typeof createPostSchema>;
export type SchedulePostInput = z.infer<typeof schedulePostSchema>;
export type SubscribeInput = z.infer<typeof subscribeSchema>;

/** The host's email preferences; only the post-live email is optional. */
/** A partial: name only the switches being changed. */
export const notificationsSchema = z
  .strictObject({ postLive: z.boolean().optional(), calendarNudges: z.boolean().optional() })
  .refine((v) => v.postLive !== undefined || v.calendarNudges !== undefined, 'Nothing to change');
export type NotificationsInput = z.infer<typeof notificationsSchema>;

/** Deleting an account: the host types their own email to confirm. */
export const accountDeleteSchema = z.strictObject({ confirm: z.string().trim().email() });
export type AccountDeleteInput = z.infer<typeof accountDeleteSchema>;

export const cardOriginSchema = z.enum(['host', 'ai', 'ai-edited']);
export type CardOrigin = z.infer<typeof cardOriginSchema>;

/** A card the host made or changed by hand. `mediaId` is a rendered flat in the app's own store. */
export const hostCardSchema = z.object({
  headline: z.string().max(200).default(''),
  body: z.string().max(600).default(''),
  altText: z.string().max(1000).default(''),
  mediaId: z.string().min(1),
  sourceMediaId: z.string().nullable().default(null),
  edit: cardEditSchema.nullable().default(null),
  tags: z.array(cardTagSchema).max(20).default([]),
  /** Who made the picture: the host (default), the AI from scratch, or the AI editing the host's. */
  origin: cardOriginSchema.optional(),
});

/** A host version: the pictures as the host arranged them. The caption is not part of it. */
export const hostVersionSchema = z.object({
  note: z.string().trim().min(1).max(80),
  parentVersionId: z.string().optional(),
  cards: z.array(hostCardSchema).min(1).max(10),
});
export type HostVersionInput = z.infer<typeof hostVersionSchema>;

/** Legacy trim/cover use source time; sequence trim/cover and all layer windows use output time. */
const renderMs = z.number().int().min(0).max(86_400_000);
export const renderSchema = z
  .strictObject({
    parentVersionId: z.string().min(1),
    trim: z.object({ startMs: renderMs, endMs: renderMs }),
    overlayMediaId: z.string().min(1).optional(),
    coverMs: renderMs,
    audio: z
      .discriminatedUnion('kind', [
        z.object({ kind: z.literal('track'), id: z.string().min(1) }),
        z.object({ kind: z.literal('media'), mediaId: z.string().min(1) }),
      ])
      .nullable(),
    layers: z.array(layerSchema).max(50),
    clips: z
      .array(
        z.strictObject({
          id: z.string().min(1).max(64),
          source: z.discriminatedUnion('kind', [
            z.strictObject({ kind: z.literal('original') }),
            z.strictObject({ kind: z.literal('media'), mediaId: z.string().min(1).max(64) }),
          ]),
          trim: z.strictObject({ startMs: renderMs, endMs: renderMs }),
        }),
      )
      .min(1)
      .max(10)
      .optional(),
    transition: z.enum(['cut', 'fade']).optional(),
    layerTimings: z
      .array(
        z.strictObject({ layerId: z.string().min(1).max(64), startMs: renderMs, endMs: renderMs }),
      )
      .max(50)
      .optional(),
    timedOverlays: z
      .array(
        z.strictObject({
          overlayMediaId: z.string().min(1).max(64),
          startMs: renderMs,
          endMs: renderMs,
        }),
      )
      .max(50)
      .optional(),
    sound: z
      .strictObject({
        originalVolume: z.number().min(0).max(1),
        musicVolume: z.number().min(0).max(1),
        musicStartMs: renderMs,
      })
      .optional(),
  })
  .superRefine((v, ctx) => {
    const duration = v.trim.endMs - v.trim.startMs;
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (v.clips) {
      if (new Set(v.clips.map((clip) => clip.id)).size !== v.clips.length)
        issue('clips', 'Give each clip a unique ID');
      if (v.clips.some((clip) => clip.trim.endMs - clip.trim.startMs < 1000))
        issue('clips', 'Select at least one second per clip');
      const total = v.clips.reduce((sum, clip) => sum + clip.trim.endMs - clip.trim.startMs, 0);
      if (v.trim.startMs !== 0 || v.trim.endMs !== total)
        issue('trim', 'Sequence trim must span 0 through the total clip duration');
    }
    const timings = v.layerTimings ?? [],
      overlays = v.timedOverlays ?? [];
    if (new Set(timings.map((timing) => timing.layerId)).size !== timings.length)
      issue('layerTimings', 'Give each timed layer a unique ID');
    if (
      (v.layerTimings || v.timedOverlays) &&
      new Set(v.layers.map((layer) => layer.id)).size !== v.layers.length
    )
      issue('layers', 'Give each layer a unique ID');
    for (const timing of timings) {
      if (!v.layers.some((layer) => layer.id === timing.layerId))
        issue('layerTimings', 'Choose an existing layer for each timing');
      if (timing.startMs >= timing.endMs || timing.endMs > duration)
        issue('layerTimings', 'Layer timing must fit inside the output');
    }
    if (
      timings.length !== overlays.length ||
      timings.some(
        (timing, i) =>
          timing.startMs !== overlays[i]?.startMs || timing.endMs !== overlays[i]?.endMs,
      )
    )
      issue(
        'timedOverlays',
        'Export one overlay per timed layer, in timing order with matching windows',
      );
    for (const overlay of overlays)
      if (overlay.startMs >= overlay.endMs || overlay.endMs > duration)
        issue('timedOverlays', 'Overlay timing must fit inside the output');
    if (v.layerTimings || v.timedOverlays) {
      const hasUntimedLayers = v.layers.some(
        (layer) => !timings.some((timing) => timing.layerId === layer.id),
      );
      if (hasUntimedLayers && !v.overlayMediaId)
        issue('overlayMediaId', 'Export the untimed layers as a static overlay');
      if (!hasUntimedLayers && v.overlayMediaId)
        issue('overlayMediaId', 'Omit the static overlay when there are no untimed layers');
    }

    if (duration < 3000 || duration > 90000)
      ctx.addIssue({ code: 'custom', path: ['trim'], message: 'Select between 3 and 90 seconds' });
    if (v.coverMs < v.trim.startMs || v.coverMs >= v.trim.endMs)
      ctx.addIssue({
        code: 'custom',
        path: ['coverMs'],
        message: 'Choose a cover inside the selected clip',
      });
  });
export type RenderInput = z.infer<typeof renderSchema>;

/** Metadata-only edits must name the current parent and its exact card positions/pictures. */
export const cardDetailsSchema = z.strictObject({
  parentVersionId: z.string().min(1),
  cards: z
    .array(
      z.strictObject({
        index: z.number().int().min(0).max(9),
        imageUrl: z.string().max(2048).nullable(),
        altText: z.string().max(1000).optional(),
        tags: z.array(cardTagSchema).max(20).optional(),
      }),
    )
    .min(1)
    .max(10),
});
export type CardDetailsInput = z.infer<typeof cardDetailsSchema>;
